<script>
import MinimalLayout from '@/components/layouts/minimal/MinimalLayout';

export default {
  name: 'EnableTfa',
  components: {
    MinimalLayout
  },
  metaInfo() {
    return { title: this.title };
  },
  data() {
    return {
      title: this.$t('Two-factor Authentication'),
      isAndroid: !!navigator.userAgent.match(/Android/i),
      isEnabled: this.$store.state.user.requiresTfa,
      isEnabling: false,
      isMac: !!navigator.platform.match(/(Mac|iPhone|iPod|iPad)/i),
      isPreparing: false,
      isRecoveryCodeStored: false,
      isVerificationInvalid: false,
      tfaDialogVisible: false,
      tfaKey: '',
      tfaLink: '',
      tfaRecoveryToken: '',
      tfaVerificationToken: ''
    };
  },
  computed: {
    authyLink() {
      return this.isMac
        ? 'https://apps.apple.com/us/app/twilio-authy/id494168017'
        : 'https://play.google.com/store/apps/details?id=com.authy.authy&hl=en_US&gl=US';
    },
    googleAuthenticatorLink() {
      return this.isMac
        ? 'https://apps.apple.com/us/app/google-authenticator/id388497605'
        : 'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en_US&gl=US';
    }
  },
  mounted() {
    this.prepare();
  },
  methods: {
    prepare() {
      this.isPreparing = true;
      this.isVerificationInvalid = false;
      this.tfaLink = '';
      this.tfaKey = '';
      this.tfaRecoveryToken = '';
      this.tfaVerificationToken = '';

      // Prepare the user for two-factor auth by generating a key and recovery code
      this.$http
        .post(`/users/${this.$store.state.user.id}/tfa`)
        .then(res => {
          // Store tokens and the auth link
          this.tfaKey = res.data.key;
          this.tfaKeyBase32 = res.data.keyBase32;
          this.tfaRecoveryToken = res.data.recoveryToken;
          this.tfaLink = res.data.authUrl;

          // Show the dialog
          this.tfaDialogVisible = true;
          this.$nextTick(() => this.$refs.verificationInput.focus());
        })
        .catch(() => {
          this.$notify({
            type: 'error',
            title: this.$t('Error'),
            message: this.$t('An error occurred. Please try your request again later.')
          });
        })
        .finally(() => (this.isPreparing = false));
    },

    enable() {
      this.isEnabling = true;
      this.$http
        .put(`/users/${this.$store.state.user.id}/tfa`, {
          token: this.tfaVerificationToken
        })
        .then(() => {
          // Update user
          this.$store.commit('setUser', Object.assign({}, this.$store.state.user, { requiresTfa: true }));
          this.$store.commit('setTfa', Object.assign({}, this.$store.state.tfa, { isVerified: true }));

          // Update the view now that it's enabled. This will bring the user to the next step in the dialog, which is to
          // write down their recovery code.
          this.isEnabled = true;
        })
        .catch(() => {
          this.$notify({
            type: 'error',
            title: this.$t('Error'),
            message: this.$t('The token you entered is not valid.')
          });
          this.isVerificationInvalid = true;
          this.$refs.verificationInput.focus();
        })
        .finally(() => (this.isEnabling = false));
    },

    done() {
      this.$router.push({ name: 'EditProfile', query: { tab: 'authentication' } });
    }
  }
};
</script>

<template>
  <minimal-layout :size="isEnabled ? 'medium' : 'large'">
    <el-form label-position="top" class="tfa-form" @submit.native.prevent="enable">
      <!-- When two-factor auth is not enabled -->
      <div v-if="!isEnabled">
        <h2>
          {{ $t('Two-factor Authentication') }}
        </h2>

        <p v-if="$store.state.user.account.requiresTfa">
          {{ $t('Your administrator requires two-factor authentication for all accounts.') }}
        </p>

        <p>
          {{
            $t(
              'This adds an extra layer of security to your account by requiring access to a known device (such as a smartphone) before logging into your account.'
            )
          }}
        </p>

        <div v-loading="isPreparing" class="qr-code">
          <sl-qr-code v-if="tfaLink" radius=".5" :value="tfaLink" size="164"></sl-qr-code>
        </div>

        <p>
          {{ $t("Please scan this code with your authentication app. If you don't have one, try one of these.") }}
        </p>

        <ul class="auth-apps">
          <li><a :href="googleAuthenticatorLink" target="_blank" rel="noopener">Google Authenticator</a></li>
          <li><a :href="authyLink" target="_blank" rel="noopener">Twilio Authy</a></li>
        </ul>

        <el-form ref="form" @submit.native.prevent="enable">
          <el-form-item
            :label="$t('Enter the code generated by your app')"
            :class="{
              'is-error': isVerificationInvalid
            }"
          >
            <el-input
              ref="verificationInput"
              v-model="tfaVerificationToken"
              type="text"
              inputmode="numeric"
              size="lg"
              class="tfa-token"
            />
          </el-form-item>

          <el-form-item>
            <el-button :loading="isEnabling" type="primary" class="el-button--block" @click="enable">
              {{ $t('Enable') }}
            </el-button>
          </el-form-item>

          <!-- Back -->
          <div v-if="!$store.state.user.account.requiresTfa" class="text-center">
            <small>
              <router-link :to="{ name: 'EditProfile', query: { tab: 'authentication' } }">
                {{ $t('Cancel') }}
              </router-link>
            </small>
          </div>
        </el-form>
      </div>

      <!-- When two-factor auth is enabled -->
      <div v-if="isEnabled">
        <p>{{ $t('Congratulations! Two-factor authentication has been enabled for your account.') }}</p>
        <pre class="tfa-recovery-token">{{ tfaRecoveryToken }}</pre>
        <p>
          {{
            $t(
              'Please store this recovery code in a secure place such as a password manager. If you lose your device, you will need it to access your account.'
            )
          }}
        </p>

        <el-form-item>
          <el-checkbox v-model="isRecoveryCodeStored" auto-complete="off">
            {{ $t('I have stored my recovery code in a secure place') }}
          </el-checkbox>
        </el-form-item>

        <el-form-item>
          <el-button
            :loading="isEnabling"
            type="primary"
            class="el-button--block"
            :disabled="!isRecoveryCodeStored"
            @click="done"
          >
            {{ $t('Continue') }}
          </el-button>
        </el-form-item>
      </div>
    </el-form>
  </minimal-layout>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.tfa-form p,
.tfa-form ul,
/deep/ .el-form-item__label {
  line-height: 1.6;
}

.qr-code {
  width: 164px;
  height: 164px;
  background: $--color-gray-10;
  margin: 1.5rem auto;
}

.auth-apps {
  list-style-position: inside;
  padding: 0;
  margin: 1rem 0;
}

.tfa-token {
  /deep/ .el-input__inner {
    font-size: 2rem;
    height: 3.5rem;
    line-height: 3.5rem;
    margin-top: 0.5rem;
  }
}

.tfa-recovery-token {
  background: $--color-gray-10;
  border-radius: 0.25rem;
  font-size: 1.25rem;
  text-align: center;
  color: $--color-gray-90;
  padding: 1rem 0;
}
</style>
